import axios from "axios";
import Vue from "vue";
import i18n from 'src/i18n'
import config from "../config";
import apiTargets from 'src/config/apiTargets';
import {requestInterceptor, errorInterceptor, successInterceptor, statusCodesToHandle} from './apiConfig';
const { API_HOST, API_VERSION } = config;
let app = new Vue({
  i18n
});
/**
 * This service is used as a base entity for CRUD operations on other entities
 */

let allInstances = []

class BaseService {
  constructor({ entity, apiTarget = apiTargets.ADMIN, path = '' } = { }) {
    let entityPath = entity ? `${entity}s` : path;
    let suffix = `${API_VERSION}/${entityPath}`;
    this.path = apiTarget ? `${apiTarget}/${suffix}`: suffix;
    this.entityPath = entityPath
    let instanceConfig = {
      baseURL: `${API_HOST}/api/${this.path}`
    };
    this._instance = axios.create(instanceConfig);
    this._instance.interceptors.response.use(successInterceptor, errorInterceptor);
    this._instance.interceptors.request.use(requestInterceptor);
    allInstances.push({
      instance: this._instance,
      suffix: `/api/${this.path}`
    })
    this.entity = entity || '';
  }

  static updateInstanceBaseUrls(newHost) {
    allInstances.forEach(({ instance, suffix }) => {
      if (API_HOST === newHost) {
        return
      }
      instance.defaults.baseURL = `${newHost}${suffix}`
    })
    if (API_HOST === newHost) {
      return
    }
    axios.defaults.baseURL = `${newHost}/api/${apiTargets.ADMIN}/${API_VERSION}`
  }

  get instance() {
    return this._instance;
  }

  /**
   * Gets a paginated list of entities including sorting, search & pagination params
   * @param page
   * @param query
   * @param sortParams
   * @param limit
   * @param path
   * @return {Promise<{data: any, total: *}>}
   */
  get({ page, query, sort, limit = 50, ...rest } = {}, path = '') {
    return this.instance
      .get(path, {
        params: {
          page,
          limit,
          search: query,
          sort,
          ...rest
        }
      })
      .then(res => {
        let result = {
          ...res,
          data: res.data !== undefined ? res.data : res,
          total: res.total !== undefined ? res.total : res.length,
        };
        return result;
      });
  }

  getById(id, params) {
    return this.instance.get(`/${id}`, {
      params
    });
  }

  async create(data, { ignoreNotifications } = {} ) {
    try {
      let res = await this.instance.post(``, data);
      this._handleSuccess({ translationKey: 'generic.create.success', ignoreNotifications, data});
      return res;
    } catch (e) {
      this._handleError({ translationKey: 'generic.create.error', error: e, ignoreNotifications});
      throw e;
    }
  }


  async update(id, data, { ignoreNotifications } = {}) {
    try {
      let res = await this.instance.put(`/${id}`, data);
      this._handleSuccess({ translationKey: 'generic.update.success', ignoreNotifications, id, data});
      return res;
    } catch (e) {
      this._handleError({ translationKey: 'generic.update.error', error: e, id: id, ignoreNotifications});
      throw e;
    }
  }

  async delete(id, { ignoreNotifications } = {}) {
    try {
      let res = await this.instance.delete(`/${id}`);
      this._handleSuccess({ translationKey: 'generic.delete.success', ignoreNotifications, id});
      return res;
    } catch (e) {
      this._handleError({ translationKey: 'generic.delete.error', error: e, id: id, ignoreNotifications});
      throw e;
    }
  }

  _handleSuccess(params) {
    if(!params.ignoreNotifications) {
      let {value, field} = this._getFieldToDisplay(params.data, params.id);
      let msg = i18n.t(params.translationKey, {
        ...params,
        fieldValue: value,
        entity: this.entity,
      });
      app.$notify({ type: 'success', message: msg});
    }
  }

  _getFieldToDisplay(data, id) {
    let fieldData = id;
    let field = 'id';
    if(!data) {
      return {
        field,
        value: fieldData
      }
    }
    let fieldsToCheck = ['title', 'name', 'fullName', 'given_name', 'provider_domain', 'uuid'];
    for(let key of fieldsToCheck) {
      let value = data[key];
      if(value) {
        fieldData = value;
        field = key;
        break;
      }
    }
    return {
      value: fieldData,
      field
    }
  }

  _handleError(params) {
    let {response} = params.error;
    // Don't handle errors for status codes that are handled by axios interceptors
    if(response && response.status && statusCodesToHandle.includes(response.status)) {
      return;
    }
    if(!params.ignoreNotifications) {
      let err = params.error.errors || i18n.t(params.translationKey, { entity: this.entity, ...params });
      app.$notify({ type: 'danger', message: err});
    }
  }

}

export default BaseService;
